import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import _ from 'lodash';

import { useNavigate } from 'react-router-dom'
import { appUrls } from '../../../utils/urls/appUrls';
import { getDraftLetters } from '../../../config/redux/actions/app';
import { Spinner } from '../../components';
import { convertDateTime, paginate } from '../../../utils/lib';
import { clearResetBot, showAttachmentFunc, setDraftLetterDetails } from '../../../config/redux/slice/appSlice';

import AttachmentView from './AttachmentView';
import Pagination from '../../components/Pagination';
import ParseHTML from '../../components/ParseHTML';

export default function DigitalLetterDraft() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const pageSize = 10;

    const [currentPage, setCurrentPage] = useState(1);
    const [draftDetails, setDraftDetails] = useState(null);

    const { userLoading, digitalLetterManagement: { draftLetters, showAttachmentModal, newLetter } } = useSelector((state) => state.app)

    useEffect(() => {
      dispatch(getDraftLetters());
    }, [dispatch, newLetter]);

    useEffect(() => {
      if (newLetter) {
        dispatch(clearResetBot());
      }       
    }, [newLetter, dispatch]);
    
    const viewDetails = useCallback((letter) =>{
      dispatch(setDraftLetterDetails({ details: letter, modal: true} ));
      navigate(`${appUrls.digitalLetterBase}?tab=sent&details=true`);
    }, [dispatch, navigate]);

    const renderReceiverMails = useCallback((letter) => {
      const [firstRecipient, ...otherRecipients] = letter.RECEIVER_EMAILS.split(',');

      return `${firstRecipient} ${otherRecipients.length > 0 ? `(+${otherRecipients.length} more)` : ''}`;
    }, []);

    const paginatedLetters = useMemo(() => {
      const sorted = _.orderBy(draftLetters, ['CREATED_AT'], ['desc']);
      const paginated = paginate(sorted, currentPage, pageSize);

      return paginated;
    }, [currentPage, draftLetters]);

    return (
      <>
        {showAttachmentModal && draftDetails && <AttachmentView filePath={draftDetails.filePath} />}

        <div className="email-right-column p-4 p-xl-5">
          <div className="email-right-header mb-5">
            <div className="head-left-options">
              <button title="Refresh" type="button" className="btn btn-icon btn-outline btn-rounded-circle" onClick={() => dispatch(getDraftLetters())}>
                <i className="mdi mdi-refresh"></i>
              </button>
            </div>

            <div style={{columnGap: '0.8rem'}} className="head-right-options d-flex align-items-center">
              {draftLetters.length > 0 && (
                <div style={{ columnGap: '0.8rem' }} className="head-right-options d-flex align-items-center">
                  <div>
                    {((currentPage * pageSize) - pageSize) + 1} - {draftLetters.length < pageSize ? draftLetters.length : currentPage * pageSize} of {draftLetters.length}
                  </div>

                  <Pagination currentPage={currentPage} itemsCount={draftLetters.length} pageSize={pageSize} onPageChange={(page) => setCurrentPage(page)} />
                </div>
              )}
            </div>
          </div>

          <div className="border border-top-0 rounded table-responsive email-list">
            {userLoading && (
              <div className="d-flex justify-content-center">
                <Spinner />
              </div>
            )}

            {paginatedLetters.length === 0 
              ? <p className='text-center mt-5'>You don't have any draft letters</p> 
              : (
                <table style={{ width: '100%' }} className="table mb-0 table-email">
                  <tbody>
                    {paginatedLetters.map((letter) => (
                      <tr key={letter.ID} onClick={() => viewDetails(letter)} className='read letter-table-row'>
                        <td className="star">
                          <div className='email-avatar'>
                            <p className='text-capitalize'>{letter?.RECEIVER_EMAILS?.[0]}</p>
                          </div>
                        </td>

                        <td className="sender-name text-dark">
                          <span className='d-inline-block' style={{ width: '187px' }} >
                            {renderReceiverMails(letter)}
                          </span>
                        </td>

                        <td>
                          <span className="text-default d-inline-block text-smoke">
                            <span className="subject text-dark">
                              {letter.LETTER_SUBJECT ? letter.LETTER_SUBJECT : '(no subject)'} -
                            </span>

                            {letter && <ParseHTML inputHtml={letter.LETTER_CONTENTS}/>}
                          </span>
                        </td>
                      
                        <td>
                          <i className={`${letter.ATTACH_DOCUMENTS ? 'hidden-on-hover' : 'attachment'} mdi mdi-paperclip`}></i>
                        </td>
                    
                        <td className="date">
                          {letter.ATTACH_DOCUMENTS && (
                            <div className='show-on-hover'>
                                <button 
                                  style={{ fontSize: '1rem', color: 'black', border: '1px solid #e5e9f2' }} 
                                  className='mr-1 btn btn-icon btn-outline btn-rounded-circle'
                                  onClick={(event) => {
                                    event.stopPropagation();

                                    setDraftDetails({ filePath: letter.ATTACH_DOCUMENTS });
                                    dispatch(showAttachmentFunc({ modal: true }));
                                  }}
                                >
                                <i className="mdi mdi-paperclip"></i>
                              </button>
                            </div>
                          )}

                          <div className={`${letter.ATTACH_DOCUMENTS ? 'hidden-on-hover' : ''}`}>
                            {convertDateTime(letter.CREATED_AT, 'MMM DD')}
                          </div>
                        </td>
                      </tr>
                    ))}
                    
                  </tbody>
                </table>
              )
            }  
          </div>
        </div>
      </>
    );
}
